import React from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Lock, Shield, Eye, AlertCircle } from 'react-feather';

const PrivacyPolicyPage = () => {
  const navigate = useNavigate();
  
  return (
    <div className="bg-white min-h-screen">
      {/* Header */}
      <header className="bg-gradient-to-r from-pink-500 to-purple-600 text-white">
        <div className="max-w-7xl mx-auto py-16 px-4 sm:py-20 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="text-center"
          >
            <h1 className="text-4xl font-extrabold tracking-tight sm:text-5xl">
              Privacy Policy
            </h1>
            <p className="mt-6 max-w-3xl mx-auto text-xl">
              Your privacy is important to us. This policy outlines how we collect, use, and protect your information.
            </p>
          </motion.div>
        </div>
      </header>

      {/* Privacy Policy Content */}
      <section className="py-16 bg-white">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
            className="prose prose-lg max-w-none"
          >
            <div className="mb-12">
              <div className="flex items-center mb-4">
                <Lock className="h-8 w-8 text-pink-500 mr-3" />
                <h2 className="text-3xl font-bold text-gray-900 m-0">Introduction</h2>
              </div>
              <p>Last updated: {new Date().toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })}</p>
              <p>
                Wedding App ("we", "our", or "us") is committed to protecting your privacy. This Privacy Policy explains how we collect, use, disclose, and safeguard your information when you visit our website or use our wedding planning application.
              </p>
              <p>
                Please read this privacy policy carefully. If you do not agree with the terms of this privacy policy, please do not access the site or use our services.
              </p>
            </div>

            <div className="mb-12">
              <div className="flex items-center mb-4">
                <Eye className="h-8 w-8 text-pink-500 mr-3" />
                <h2 className="text-3xl font-bold text-gray-900 m-0">Information We Collect</h2>
              </div>
              
              <h3 className="text-xl font-semibold">Personal Information</h3>
              <p>
                We may collect personal information that you voluntarily provide to us when you register on our platform, express interest in obtaining information about us or our products, or otherwise contact us. The personal information we collect may include:
              </p>
              <ul>
                <li>Name, email address, and contact information</li>
                <li>Wedding details such as date, location, and guest information</li>
                <li>Account credentials</li>
                <li>Payment and billing information</li>
                <li>Preferences and settings</li>
              </ul>

              <h3 className="text-xl font-semibold">Automatically Collected Information</h3>
              <p>
                When you visit our website or use our application, we may automatically collect certain information about your device, including:
              </p>
              <ul>
                <li>IP address and browser type</li>
                <li>Operating system</li>
                <li>Pages visited and time spent</li>
                <li>Referral source</li>
                <li>Usage patterns and preferences</li>
              </ul>
            </div>

            <div className="mb-12">
              <div className="flex items-center mb-4">
                <Shield className="h-8 w-8 text-pink-500 mr-3" />
                <h2 className="text-3xl font-bold text-gray-900 m-0">How We Use Your Information</h2>
              </div>
              
              <p>We may use the information we collect for various purposes, including:</p>
              <ul>
                <li>Providing and maintaining our services</li>
                <li>Personalizing your experience</li>
                <li>Processing transactions and managing your account</li>
                <li>Communicating with you about updates, promotions, and support</li>
                <li>Improving our website and services</li>
                <li>Conducting analysis and research to enhance user experience</li>
                <li>Ensuring the security and integrity of our platform</li>
                <li>Complying with legal obligations</li>
              </ul>
            </div>

            <div className="mb-12">
              <div className="flex items-center mb-4">
                <AlertCircle className="h-8 w-8 text-pink-500 mr-3" />
                <h2 className="text-3xl font-bold text-gray-900 m-0">Cookies and Tracking Technologies</h2>
              </div>
              
              <p>
                We use cookies and similar tracking technologies to collect information about your browsing activities and to analyze website traffic. Cookies are small data files that are placed on your device when you visit a website.
              </p>
              
              <h3 className="text-xl font-semibold">Types of Cookies We Use:</h3>
              <ul>
                <li><strong>Essential Cookies:</strong> Necessary for the basic functionality of our website.</li>
                <li><strong>Analytical/Performance Cookies:</strong> Help us understand how visitors interact with our website.</li>
                <li><strong>Functionality Cookies:</strong> Allow us to remember choices you make and provide enhanced features.</li>
                <li><strong>Targeting Cookies:</strong> Record your visit to our website, the pages you have visited, and the links you have followed.</li>
              </ul>
              
              <p>
                You can set your browser to refuse all or some browser cookies, or to alert you when websites set or access cookies. If you disable or refuse cookies, please note that some parts of our website may become inaccessible or not function properly.
              </p>
            </div>

            <div className="mb-12">
              <h2 className="text-3xl font-bold text-gray-900 mb-4">Disclosure of Your Information</h2>
              <p>
                We may share your information in the following situations:
              </p>
              <ul>
                <li><strong>Business Transfers:</strong> We may share or transfer your information in connection with a merger, acquisition, or sale of all or a portion of our assets.</li>
                <li><strong>Third-Party Service Providers:</strong> We may share your information with third-party vendors, service providers, and contractors who perform services for us.</li>
                <li><strong>Legal Requirements:</strong> We may disclose your information if required to do so by law or in response to valid requests by public authorities.</li>
                <li><strong>With Your Consent:</strong> We may disclose your information for any other purpose with your consent.</li>
              </ul>
            </div>

            <div className="mb-12">
              <h2 className="text-3xl font-bold text-gray-900 mb-4">Your Rights</h2>
              <p>
                Depending on your location, you may have certain rights regarding your personal information, including:
              </p>
              <ul>
                <li>The right to access your personal information</li>
                <li>The right to rectify inaccurate information</li>
                <li>The right to request deletion of your personal information</li>
                <li>The right to restrict or object to our processing of your information</li>
                <li>The right to data portability</li>
                <li>The right to withdraw consent</li>
              </ul>
              <p>
                To exercise these rights, please contact us using the information provided in the "Contact Us" section below.
              </p>
            </div>

            <div className="mb-12">
              <h2 className="text-3xl font-bold text-gray-900 mb-4">Security of Your Information</h2>
              <p>
                We use appropriate technical and organizational measures to protect your personal information. However, no electronic transmission or storage of information can be guaranteed to be 100% secure, so we cannot promise or guarantee absolute security.
              </p>
            </div>

            <div className="mb-12">
              <h2 className="text-3xl font-bold text-gray-900 mb-4">Changes to This Privacy Policy</h2>
              <p>
                We may update our Privacy Policy from time to time. We will notify you of any changes by posting the new Privacy Policy on this page and updating the "Last Updated" date. You are advised to review this Privacy Policy periodically for any changes.
              </p>
            </div>

            <div>
              <h2 className="text-3xl font-bold text-gray-900 mb-4">Contact Us</h2>
              <p>
                If you have any questions or concerns about this Privacy Policy, please contact us at:
              </p>
              <p>
                <strong>Email:</strong> privacy@weddingapp.com<br />
                <strong>Address:</strong> 123 Wedding Avenue, New York, NY 10001<br />
                <strong>Phone:</strong> +1 (555) 123-4567
              </p>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-gray-900 text-white py-8">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <p className="text-gray-400">© {new Date().getFullYear()} Wedding App. All rights reserved.</p>
          <div className="mt-4">
            <button 
              onClick={() => navigate('/')}
              className="text-gray-400 hover:text-pink-300 transition-colors mx-2"
            >
              Home
            </button>
            <button 
              onClick={() => navigate('/about')}
              className="text-gray-400 hover:text-pink-300 transition-colors mx-2"
            >
              About Us
            </button>
            <button 
              onClick={() => navigate('/contact')}
              className="text-gray-400 hover:text-pink-300 transition-colors mx-2"
            >
              Contact
            </button>
            <button 
              onClick={() => navigate('/terms')}
              className="text-gray-400 hover:text-pink-300 transition-colors mx-2"
            >
              Terms of Service
            </button>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default PrivacyPolicyPage;